import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MagneticButton } from './MagneticButton';
import logoHorizontal from '../assets/no BG New Cura Logo.png';

interface NavItem {
  label: string;
  href: string;
}

interface NavigationProps {
  menuItems: NavItem[];
  scrollToSection: (sectionId: string) => void;
  scrollToTop: () => void;
  hideLogo?: boolean;
  forceWhiteBackground?: boolean;
  ctaText?: string;
  ctaHref?: string;
  centerMenu?: boolean;
}

export const Navigation: React.FC<NavigationProps> = ({
  menuItems,
  scrollToSection,
  scrollToTop,
  hideLogo = false,
  forceWhiteBackground = false,
  ctaText = "Partner With Us",
  ctaHref = "mailto:founders@mycura.org?subject=Partnership%20Inquiry",
  centerMenu = false
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (href: string) => {
    if (href.startsWith('/')) {
      navigate(href);
      setIsMobileMenuOpen(false);
      return;
    }
    if (href.startsWith('#')) {
      const sectionId = href.slice(1);
      // If we're not on the homepage, navigate there first
      if (window.location.pathname !== '/') {
        navigate('/');
        // Wait for navigation to complete, then scroll
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        // Already on homepage, just scroll
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
      setIsMobileMenuOpen(false);
      return;
    }
    // Fallback for full URLs
    if (/^https?:\/\//.test(href)) {
      window.open(href, '_blank', 'noopener,noreferrer');
      setIsMobileMenuOpen(false);
    }
  };

  const handleLogoClick = () => {
    // If we're not on the homepage, navigate to it first
    if (window.location.pathname !== '/') {
      navigate('/');
      // Scroll to top after navigation completes
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      // If already on homepage, just scroll to top
      scrollToTop();
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.nav
      className="fixed top-0 left-0 right-0 z-50"
    >

      <div className="w-full relative z-[52]">
        <div className={`transition-all duration-300 ${isMobileMenuOpen
          ? 'bg-white/95 backdrop-blur-xl border-b border-slate-100'
          : forceWhiteBackground
            ? 'bg-white shadow-sm'
            : isScrolled
              ? 'backdrop-blur-xl shadow-sm'
              : 'bg-transparent'
          }`}
          style={
            isMobileMenuOpen
              ? {}
              : forceWhiteBackground
                ? {}
                : (isScrolled ? {
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 85%, transparent 100%)',
                  maskImage: 'linear-gradient(to bottom, black 0%, black 85%, transparent 100%)',
                  WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 85%, transparent 100%)'
                } : {})
          }>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-[68px]">
              {/* Logo */}
              {!hideLogo && (
                <div className="flex-1 flex justify-start">
                  <button onClick={handleLogoClick} className="flex items-center">
                    <img
                      src={logoHorizontal}
                      alt="Cura Logo"
                      className="h-8 w-auto"
                    />
                  </button>
                </div>
              )}

              {/* Desktop Navigation - Centered */}
              <div className={`hidden md:flex ${centerMenu ? 'flex-1 justify-center' : `flex-1 ${hideLogo ? 'justify-start' : 'justify-center'}`}`}>
                <div className="flex items-center space-x-8">
                  {menuItems.map((item) => {
                    // Show "Home" instead of "Features" when not on homepage
                    const isOnHomepage = location.pathname === '/';
                    const isFeaturesLink = item.href === '#features' || item.href === '/#features';
                    const displayLabel = isFeaturesLink && !isOnHomepage ? 'Home' : item.label;
                    const displayHref = isFeaturesLink && !isOnHomepage ? '/' : item.href;

                    return (
                      <button
                        key={item.href}
                        onClick={() => handleNavigation(displayHref)}
                        className={`hover:text-[#003c83] transition-colors duration-200 font-medium text-base whitespace-nowrap ${forceWhiteBackground ? 'text-black' : (isScrolled ? 'text-black' : 'text-black')
                          }`}
                      >
                        {displayLabel}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* CTA Buttons */}
              <div className={`hidden md:flex ${centerMenu ? 'absolute right-4 sm:right-6 lg:right-8' : 'flex-1 justify-end'} items-center`}>
                <MagneticButton
                  href={ctaHref}
                  target={/^https?:\/\//.test(ctaHref) ? '_blank' : undefined}
                  rel={/^https?:\/\//.test(ctaHref) ? 'noopener noreferrer' : undefined}
                  className={`inline-flex items-center px-6 py-2.5 text-base font-medium rounded-lg transition-all duration-300 shadow-md hover:shadow-lg ${forceWhiteBackground
                    ? 'text-[#003c83] bg-white border-2 border-[#003c83] hover:bg-[#003c83] hover:text-white'
                    : 'text-white bg-gradient-to-r from-[#003c83] to-[#0052b4] hover:from-[#002d62] hover:to-[#003c83]'
                    }`}
                  strength={0.2}
                >
                  {ctaText}
                </MagneticButton>
              </div>

              {/* Mobile menu button */}
              <div className={`flex md:hidden ${hideLogo ? '' : 'ml-auto'}`}>
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className={`inline-flex items-center justify-center p-2 rounded-md hover:text-[#003c83] focus:outline-none ${forceWhiteBackground ? 'text-gray-900' : 'text-gray-900'
                    }`}
                >
                  <span className="sr-only">Open main menu</span>
                  {!isMobileMenuOpen ? (
                    <svg
                      className="block h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="block h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop for click-outside */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="md:hidden fixed inset-0 bg-black/20 backdrop-blur-sm z-30"
            />

            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`md:hidden absolute left-0 right-0 top-full z-40 overflow-hidden border-b border-slate-200 ${forceWhiteBackground
                ? 'bg-white'
                : 'bg-white/95 backdrop-blur-xl'
                }`}
            >
              <div className="px-4 pt-4 pb-4 space-y-6">
                <div className="space-y-2">
                  {menuItems.map((item, index) => {
                    const isOnHomepage = location.pathname === '/';
                    const isFeaturesLink = item.href === '#features' || item.href === '/#features';
                    const displayLabel = isFeaturesLink && !isOnHomepage ? 'Home' : item.label;
                    const displayHref = isFeaturesLink && !isOnHomepage ? '/' : item.href;

                    return (
                      <motion.button
                        transition={{ delay: index * 0.05 }}
                        key={item.href}
                        onClick={() => handleNavigation(displayHref)}
                        className="block w-full text-left px-4 py-3 text-lg font-medium text-slate-800 hover:text-blue-600 hover:bg-blue-50/50 rounded-xl transition-all duration-200"
                      >
                        {displayLabel}
                      </motion.button>
                    );
                  })}
                </div>

                <div className="pt-4 space-y-3 border-t border-slate-100">
                  <button
                    onClick={() => handleNavigation('/enterprise-api')}
                    className="flex items-center justify-center w-full px-4 py-3.5 text-base font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors"
                  >
                    Explore Partnerships
                  </button>
                  <a
                    href="mailto:founders@mycura.org?subject=Partnership%20Inquiry"
                    className="flex items-center justify-center w-full px-4 py-3.5 text-base font-semibold text-white bg-[#003c83] hover:bg-[#002d62] rounded-xl shadow-lg shadow-blue-900/20 transition-all"
                  >
                    Partner With Us
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
